import { faExclamationCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Button,
  Divider,
  Dropdown,
  DropdownButton,
  DropdownOption,
  DropdownOptionList,
  Flex,
  InlineAlert,
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Text,
  TextButton,
  TextWithIcon,
  Tooltip,
} from '@modusign/design-system';
import { useGetRole, useListRoles } from '@modusign/openapi-client-user';
import { Fragment } from 'react';
import { useHistory } from 'react-router';

import { useDeferredState } from '@hooks';

import { settingsWorkspaceRoutes } from '@app/features/workspaceManagement/routes';
import { WorkspaceRole } from '@app/features/workspaceManagement/types';
import { getWorkspacePermissionsByCategoryEntries } from '@app/features/workspaceManagement/workspaceRole/constant/RolePermissionsView';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import useResponsiveValue from '@app/hooks/useResponsiveValue';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

type RoleSelection =
  | {
      id: string;
      name: string;
      description: string | null;
    }
  | {
      id: 'non-granted';
      name: '역할 없음';
      description: null;
    };

const NONE_GRANTED: RoleSelection = {
  id: 'non-granted',
  name: '역할 없음',
  description: null,
};

interface GrantWorkspaceRoleModalProps {
  initialState?: WorkspaceRole['id'];
  isOpen: boolean;
  title: string;
  onClose: () => void;
  onConfirm: (role: { id: string; name: string }) => void;
  onReset?: () => void;
}

export default function GrantWorkspaceRoleModal({
  initialState,
  isOpen,
  title,
  onClose,
  onConfirm,
  onReset,
}: GrantWorkspaceRoleModalProps) {
  const history = useHistory();
  const workspaceId = useWorkspaceId();
  const roles = useListRoles(workspaceId);

  // 최고관리자는 노출되지 않습니다.
  const rolesNotContainAdmin = roles.data?.roles.filter((r) => r.type !== 'ADMIN') ?? [];

  const selections = [...(onReset ? [NONE_GRANTED] : []), ...rolesNotContainAdmin];

  const [selectedRole, setSelectedRole] = useDeferredState<RoleSelection | undefined>(
    rolesNotContainAdmin.find((r) => r.id === initialState)
  );

  const { isPermitted } = useMemberPermissions('MANAGE_MEMBER');

  return (
    <Modal isOpen={isOpen} maxWidth="xmd" onClose={onClose}>
      <ModalTitle>{title}</ModalTitle>
      <ModalContent gap={6}>
        {rolesNotContainAdmin.length === 0 && (
          <InlineAlert sx={{ width: '100%' }} variant="warning">
            만들어진 역할이 없습니다.
            <Tooltip content="권한 없음" disabled={isPermitted}>
              <TextButton
                disabled={!isPermitted}
                ml={2}
                onClick={() => history.push(settingsWorkspaceRoutes.roleForm)}
              >
                새 역할 만들기
              </TextButton>
            </Tooltip>
          </InlineAlert>
        )}
        <Dropdown
          placeholder="역할 선택"
          onChange={(v) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            setSelectedRole(selections.find((r) => r.id === v)!);
          }}
        >
          <DropdownButton>{selectedRole?.name ?? ''}</DropdownButton>
          <DropdownOptionList bg="menuBackgroundNeutralBase">
            {selections.map((r) => (
              <DropdownOption key={r.id} value={r.id}>
                {r.name}
              </DropdownOption>
            ))}
          </DropdownOptionList>
        </Dropdown>

        {selectedRole?.description && (
          <Text color="textNeutralLight" mt={4} px={4}>
            {selectedRole.description}
          </Text>
        )}
        {selectedRole && selectedRole.id !== NONE_GRANTED.id && (
          <>
            <InlineAlert sx={{ my: 7 }} variant="warning">
              ‘전체 조회’ 권한이 있으면 모든 목록과 상세 내용을 볼 수 있습니다.
            </InlineAlert>

            <PermissionGroupListSection roleId={selectedRole.id} />
          </>
        )}
      </ModalContent>

      <ModalFooter>
        <Button weight="light" onClick={onClose}>
          취소
        </Button>
        <Button
          disabled={!selectedRole}
          ml={3}
          variant="primary"
          weight="light"
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          onClick={() => {
            if (!selectedRole) return;

            if (selectedRole.id === 'non-granted') {
              onReset?.();
            } else {
              onConfirm(selectedRole);
            }
          }}
        >
          설정
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function PermissionGroupListSection({ roleId }: { roleId: WorkspaceRole['id'] }) {
  const workspaceId = useWorkspaceId();
  const role = useGetRole(workspaceId, roleId);
  const enabledPermissions = role.data?.permissions ?? [];

  const isDesktopWidth = useResponsiveValue([false, false, true]);

  const workspacePermissionByCategory = getWorkspacePermissionsByCategoryEntries(
    enabledPermissions,
    isDesktopWidth ? 'map' : 'filter'
  );

  return (
    <Flex
      as="section"
      backgroundColor="boxBackgroundNeutralBase"
      border="1px solid"
      borderColor="boxBorderNeutralLight"
      flexDirection="column"
      gap={4}
      px="20px"
      py="16px"
    >
      {workspacePermissionByCategory.map(([groupName, permissions], index) =>
        isDesktopWidth ? (
          // eslint-disable-next-line react/no-array-index-key
          <Fragment key={index}>
            {index !== 0 && <Divider />}
            <PermissionGroupLayout roleName={groupName}>
              {permissions.map((permission, idx) => {
                if (permission.name === 'READ_ALL_DOCUMENT') return null;
                return (
                  <PermissionGroupEntries
                    descriptions={permission.descriptions as ReadonlyArray<string>}
                    enabled={enabledPermissions.some((enabled) => enabled.name === permission.name)}
                    isCautious={permission.caution}
                    // eslint-disable-next-line react/no-array-index-key
                    key={idx}
                    roleName={permission.displayName}
                  />
                );
              })}
            </PermissionGroupLayout>
          </Fragment>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <Flex as="section" gap={3} key={index}>
            <Text>
              <Text fontWeight="bold">{groupName} : </Text>
              <Text>{permissions.map((p) => p.displayName).join(' / ')}</Text>
            </Text>
          </Flex>
        )
      )}
    </Flex>
  );
}

function PermissionGroupLayout({
  children,
  roleName,
}: {
  children: React.ReactNode;
  roleName: string;
}) {
  return (
    <Flex as="section" gap={3}>
      <Text fontWeight="medium" py={2} sx={{ flex: '0 0 120px' }}>
        {roleName}
      </Text>
      <Flex flexWrap="wrap" width="100%">
        {children}
      </Flex>
    </Flex>
  );
}

function PermissionGroupEntries({
  descriptions,
  enabled,
  isCautious,
  roleName,
}: {
  descriptions: ReadonlyArray<string>;
  roleName: string;
  isCautious: boolean;
  enabled: boolean;
}) {
  return (
    <Flex alignItems="center" flex="0 0 190px" gap={2} py={2}>
      <Tooltip
        content={
          descriptions.length > 1 ? (
            descriptions.map((line) => (
              <Fragment key={line}>
                <Text>- {line}</Text>
                <br />
              </Fragment>
            ))
          ) : (
            <Text>{descriptions}</Text>
          )
        }
        placement="right"
        scale="md"
      >
        <div>
          <TextWithIcon
            color={enabled ? 'textNeutralSlightlyLight' : 'textNeutralUltraLight'}
            fontWeight="medium"
            rightIcon={
              enabled &&
              isCautious && (
                <Box as={FontAwesomeIcon} color="iconPrimaryDark" icon={faExclamationCircle} />
              )
            }
          >
            {roleName}
          </TextWithIcon>
        </div>
      </Tooltip>
    </Flex>
  );
}
